/**
 * Sample IELTS Academic exam_body for frontend-first dev (REACT_APP_USE_MOCK).
 * Exercises every question-type the registry supports, plus Writing & Speaking.
 * Mirrors the backend JSON shape documented in the plan.
 */
export const sampleExamBody = {
  type: 'ielts',
  title: 'IELTS Academic Mock #1 (Sample)',
  coverImage: '',
  bandScoreTable: {
    listening: {
      6: 6, 7: 6.5, 8: 7, 9: 7.5, 10: 8,
    },
    reading: {
      6: 6, 7: 6.5, 8: 7, 9: 7.5, 10: 8,
    },
  },
  skills: {
    listening: {
      durationInSecond: 30 * 60,
      parts: [
        {
          audio: '',
          instruction: 'Questions 1–4: Listen and answer.',
          questions: [
            {
              type: 'mcq',
              questionNumberMark: 1,
              text: 'What time does the museum open?',
              options: { A: '9 a.m.', B: '10 a.m.', C: '11 a.m.' },
              correctAnswer: 'B',
            },
            {
              type: 'multi-select',
              questionNumberMark: 2,
              text: 'Which TWO facilities are mentioned?',
              maxChoices: 2,
              options: {
                A: 'Café',
                B: 'Car park',
                C: 'Gift shop',
                D: 'Library',
              },
              correctAnswer: ['A', 'C'],
            },
            {
              type: 'gap-fill',
              questionNumberMark: 3,
              text: 'The tour guide’s name is ______.',
              wordLimit: 2,
              acceptedAnswers: ['Sarah', 'sarah'],
            },
            {
              type: 'short-answer',
              questionNumberMark: 4,
              text: 'How much is the student ticket?',
              wordLimit: 3,
              acceptedAnswers: ['5 pounds', '£5'],
            },
          ],
        },
      ],
    },
    reading: {
      durationInSecond: 60 * 60,
      passages: [
        {
          text: '## The History of Tea\n\nTea was first discovered in ancient China. '
            + 'Over centuries it spread across trade routes, becoming one of the most '
            + 'consumed beverages in the world. Its cultivation shaped economies and '
            + 'cultures alike.',
          instruction: 'Questions 5–11: Answer based on the passage.',
          questions: [
            {
              type: 'tfng',
              questionNumberMark: 5,
              text: 'Tea was first discovered in India.',
              correctAnswer: 'False',
            },
            {
              type: 'ynng',
              questionNumberMark: 6,
              text: 'The writer believes tea shaped cultures.',
              correctAnswer: 'Yes',
            },
            {
              type: 'matching',
              questionNumberMark: 7,
              text: 'Match the region to its description.',
              optionPool: { A: 'China', B: 'India', C: 'Japan' },
              correctAnswer: 'A',
            },
            {
              type: 'matching-headings',
              questionNumberMark: 8,
              text: 'Choose the heading for paragraph 1.',
              optionPool: {
                A: 'Origins of tea',
                B: 'Modern consumption',
                C: 'Economic impact',
              },
              correctAnswer: 'A',
            },
            {
              type: 'sentence-completion',
              questionNumberMark: 9,
              text: 'Tea spread across ______ routes.',
              wordLimit: 1,
              acceptedAnswers: ['trade'],
            },
            {
              type: 'summary-completion',
              questionNumberMark: 10,
              intro: 'Complete the summary using the word bank.',
              wordBank: ['China', 'trade', 'economies', 'cultures'],
              subQuestions: [
                {
                  type: 'gap-fill',
                  questionNumberMark: '10a',
                  text: 'Tea originated in ______.',
                  acceptedAnswers: ['China'],
                },
                {
                  type: 'gap-fill',
                  questionNumberMark: '10b',
                  text: 'It shaped ______ and cultures.',
                  acceptedAnswers: ['economies'],
                },
              ],
            },
            {
              type: 'diagram-label',
              questionNumberMark: 11,
              intro: 'Label the diagram of the tea plant.',
              image: '',
              subQuestions: [
                {
                  type: 'gap-fill',
                  questionNumberMark: '11a',
                  text: 'Top part: ______',
                  acceptedAnswers: ['leaf'],
                },
                {
                  type: 'gap-fill',
                  questionNumberMark: '11b',
                  text: 'Bottom part: ______',
                  acceptedAnswers: ['root'],
                },
              ],
            },
          ],
        },
      ],
    },
    writing: {
      durationInSecond: 60 * 60,
      tasks: [
        {
          taskNumber: 1,
          minWords: 150,
          prompt: 'The chart below shows coffee consumption in three countries. '
            + 'Summarise the information by selecting and reporting the main features.',
          image: '',
        },
        {
          taskNumber: 2,
          minWords: 250,
          prompt: 'Some people think technology has made our lives too complex. '
            + 'To what extent do you agree or disagree?',
        },
      ],
    },
    speaking: {
      durationInSecond: 14 * 60,
      parts: [
        {
          partNumber: 1,
          prompts: ['Where are you from?', 'Do you enjoy your hometown? Why?'],
        },
        {
          partNumber: 2,
          cueCard: 'Describe a book you recently read.\n\nYou should say:\n'
            + '- what it was about\n- why you chose it\n- and explain whether you would recommend it.',
        },
        {
          partNumber: 3,
          prompts: ['How have reading habits changed over time?', 'Will printed books disappear?'],
        },
      ],
    },
  },
};

// A listing entry (as returned by GET /ielts/exam).
export const sampleExamListItem = {
  id: 1,
  title: sampleExamBody.title,
  coverImage: '',
  sequence: 1,
};

// A graded result (as returned by GET /ielts/examResult/:id), demonstrating the
// band report: L/R auto-scored, W/S still pending grading.
export const sampleResult = {
  id: 101,
  examId: 1,
  testedAt: '2026-06-26T00:00:00Z',
  examName: sampleExamBody.title,
  examBody: sampleExamBody,
  score: {
    bands: {
      listening: 7,
      reading: 6.5,
      writing: null,
      speaking: null,
    },
    overall: null,
    responses: {
      1: { usersAnswer: 'B', correctAnswer: 'B', isCorrect: true },
      2: { usersAnswer: 'A,C', correctAnswer: 'A,C', isCorrect: true },
      3: { usersAnswer: 'Sarah', correctAnswer: 'Sarah', isCorrect: true },
      4: { usersAnswer: '', correctAnswer: '£5', isCorrect: false },
      5: { usersAnswer: 'False', correctAnswer: 'False', isCorrect: true },
    },
  },
};
